import React, { ReactNode, lazy } from 'react'

// import AppLayout from '../components/Layouts/AppLayout'
// import AdminLayout from '../components/Layouts/AdminLayout'
import { IAppRouteSetting } from '../types'
import { UserRolesEnum } from '../user'

import { AuthGuard, OnboardingGuard, RoleGuard } from './guards'
import { ADMIN_PATHS, APP_PATHS } from './paths'

const AppLayout = lazy(() => import('../components/Layouts/UserLayout'))
const AdminLayout = lazy(() => import('../components/Layouts/AdminLayout'))

export const NonAuthenticatedRoutes: Array<IAppRouteSetting> = [
  {
    exact: true,
    path: APP_PATHS.auth,
    component: lazy(() => import('../pages/SignIn/SignInPage')),
  },
  {
    exact: true,
    path: APP_PATHS.logout,
    component: lazy(() => import('../pages/Logout/LogoutPage')),
  },
  {
    exact: true,
    path: APP_PATHS.signup,
    component: lazy(() => import('../pages/Signup/Signup')),
  },
  {
    exact: true,
    path: APP_PATHS.forgotPassword,
    component: lazy(() => import('../pages/ForgotPassword/ForgotPassword')),
  },
  {
    exact: true,
    path: APP_PATHS.resetPassword,
    component: lazy(() => import('../pages/ResetPassword/ResetPassword')),
  },
  {
    exact: true,
    path: APP_PATHS.emailConfirmation,
    component: lazy(() => import('../pages/EmailConfirmation/EmailConfirmationPage')),
  },
  {
    exact: true,
    path: APP_PATHS.contact,
    component: lazy(() => import('../pages/Contact/ContactPage')),
  },
  {
    exact: true,
    path: APP_PATHS.accountDeletion,
    component: lazy(() => import('../pages/AccountDeletion')),
  },
  {
    exact: true,
    path: APP_PATHS.error,
    component: lazy(() => import('../pages/Error/Error')),
  },
]

export const BusinessApplicationRoutes: Array<IAppRouteSetting> = [
  {
    exact: true,
    path: APP_PATHS.dashboard,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/Dashboard/DashboardPage')),
  },
  {
    exact: true,
    path: APP_PATHS.dailyStats,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/DailyStats/DailyStatsPage')),
  },
  {
    exact: true,
    path: APP_PATHS.aiMarketInsights,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/AIMarketInsights/AIMarketInsightsPage')),
  },
  {
    exact: true,
    path: APP_PATHS.spreadCheck,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/SpreadCheck/SpreadCheckPage')),
  },
  {
    exact: true,
    path: APP_PATHS.tradingLog,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/TradingLog/TradingLogPage')),
  },
  {
    exact: true,
    path: APP_PATHS.cashFlow,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/CashFlow/CashFlowPage')),
  },
  {
    exact: true,
    path: APP_PATHS.tradingPlan,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/TradingPlan/TradingPlanPage')),
  },
  {
    exact: true,
    path: APP_PATHS.reports,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/Reports/ReportsPage')),
  },
  {
    exact: true,
    path: APP_PATHS.settings,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/Settings/SettingsPage')),
  },
  {
    exact: true,
    path: APP_PATHS.profile,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/Profile/ProfilePage')),
  },
  {
    exact: true,
    path: APP_PATHS.importTrades,
    layout: AppLayout,
    guard: AuthGuard,
    component: lazy(() => import('../pages/ImportTrades/ImportTradesPage')),
  },
  {
    exact: true,
    path: APP_PATHS.createReport,
    guard: AuthGuard,
    component: lazy(() => import('../pages/Reports/CreateReport')),
  },
  // {
  //   exact: false,
  //   layout: AppLayout,
  //   path: APP_PATHS.education,
  //   guard: AuthGuard,
  //   component: lazy(() => import('../pages/Education/EducationPage')),
  // },
  // {
  //   exact: true,
  //   layout: AppLayout,
  //   path: APP_PATHS.webinars,
  //   guard: AuthGuard,
  //   component: lazy(() => import('../pages/Webinars/WebinarsPage')),
  // },
  // {
  //   layout: AppLayout,
  //   path: APP_PATHS.lesson,
  //   guard: AuthGuard,
  //   component: lazy(() => import('../pages/Education/LessonPage')),
  // },
  // {
  //   layout: AppLayout,
  //   path: APP_PATHS.completedModule,
  //   guard: AuthGuard,
  //   component: lazy(() => import('../pages/Education/CompletedModulePage')),
  // },
  // {
  //   layout: AppLayout,
  //   path: APP_PATHS.completedQuiz,
  //   guard: AuthGuard,
  //   component: lazy(() => import('../pages/Education/CompletedQuizPage')),
  // },
  // {
  //   layout: AppLayout,
  //   path: APP_PATHS.notCompletedQuiz,
  //   guard: AuthGuard,
  //   component: lazy(() => import('../pages/Education/NotCompletedQuizPage')),
  // },
  // {
  //   layout: AppLayout,
  //   path: APP_PATHS.webinar,
  //   guard: AuthGuard,
  //   component: lazy(() => import('../pages/Webinars/WebinarPage')),
  // },
  {
    exact: true,
    path: APP_PATHS.onboarding,
    guard: OnboardingGuard,
    component: lazy(() => import('../pages/OnboardingStepper/OnboardingPage')),
  },
]

export const AuthenticatedRoutes: Array<IAppRouteSetting> = [...BusinessApplicationRoutes]

export const AdminDashboardRoutes: Array<IAppRouteSetting> = [
  {
    exact: true,
    path: ADMIN_PATHS.adminDashboard,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/AdminDashboard/AdminDashboardPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.activationFunnel,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/ActivationFunnel/ActivationFunnelPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.featureUsage,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/FeatureUsage/FeatureUsagePage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.traderProfitability,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/TraderProfitability/TraderProfitabilityPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.churnSignals,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/ChurnSignals/ChurnSignalsPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.mailchimp,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/Mailchimp/MailchimpPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.activeSessions,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/ActiveSessions/ActiveSessionsPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.adminEvents,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/Events/AdminEventsPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.questionnaireAnalytics,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(
      () => import('../pages/Admin/QuestionnaireAnalytics/QuestionnaireAnalyticsPage'),
    ),
  },
  {
    exact: true,
    path: ADMIN_PATHS.linkClickAnalytics,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/LinkClickAnalytics/LinkClickAnalyticsPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.users,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/Users/UsersPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.commissionBenchmark,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/CommissionBenchmark/CommissionBenchmarkPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.accounts,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/Accounts/AccountsPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.propBreachedAccounts,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/PropBreachedAccounts/PropBreachedAccountsPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.brokerIdentifiers,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/BrokerIdentifiers/BrokerIdentifiersPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.blockedIps,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/BlockedIps/BlockedIpsPage')),
  },
  {
    path: ADMIN_PATHS.accountConnectionLogs,
    layout: AdminLayout,
    guard: ({ children }) => <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>,
    component: lazy(() => import('../pages/Admin/AccountConnectionLogs/AccountConnectionLogsPage')),
  },
  {
    exact: true,
    path: ADMIN_PATHS.cronControl,
    layout: AdminLayout,
    guard: ({ children }: { children: ReactNode }) => (
      <RoleGuard roles={[UserRolesEnum.ADMIN]}>{children}</RoleGuard>
    ),
    component: lazy(() => import('../pages/Admin/CronControl/CronControlPage')),
  },
]
